import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Modal } from 'antd'
import { useDispatch, useStore } from 'react-redux'
import menuBuilderMenusCopy from 'src/constants/menuBuilderCopy'
import { updateMenuSalesMix } from 'src/features/MenuBuilderSlice'
import {
  readQtyForRecipe,
  salesMixDraftHasLocalEdits,
  salesMixQtyDraftHasInvalidValues,
} from 'src/utils/menuSalesMix'
import {
  buildMenuSalesMixPanelPreview,
  buildQtyDraftAfterSalesMixSave,
  buildSalesMixSaveEntries,
  mapSalesMixToPanelQty,
  resolveSavedMenuAfterSalesMixSave,
  salesMixHasPositiveQty,
  toMenuSalesMixPanelRecipes,
} from 'src/utils/menuSalesMixPanelData'
import MenuSalesMixPanel from './MenuSalesMixPanel'

/**
 * Local sales-mix qty state for one menu edit session.
 *
 * Tab behaviour: parent only renders this when the Sales mix tab is active,
 * so leaving the tab unmounts the component and remounting re-reads
 * `initialSalesMix` from `currentMenu.salesMix`.
 * Successful save hydrates qty from the PATCH response / Redux menu state.
 */
const MenuSalesMixTab = ({
  menuId,
  panelRecipes = [],
  initialSalesMix = [],
  disabled = false,
  onPreviewChange,
}) => {
  const dispatch = useDispatch()
  const store = useStore()
  const copy = menuBuilderMenusCopy
  const hydratedMenuIdRef = useRef(null)
  const userEditedRef = useRef(false)
  const [qtyByRecipe, setQtyByRecipe] = useState(() =>
    mapSalesMixToPanelQty(panelRecipes, initialSalesMix),
  )
  const [saving, setSaving] = useState(false)
  const qtyByRecipeRef = useRef(qtyByRecipe)

  useEffect(() => {
    qtyByRecipeRef.current = qtyByRecipe
  }, [qtyByRecipe])

  useEffect(
    () => () => {
      hydratedMenuIdRef.current = null
      userEditedRef.current = false
    },
    [],
  )

  useEffect(() => {
    if (!menuId) {
      return
    }

    const fromMenu = mapSalesMixToPanelQty(panelRecipes, initialSalesMix)

    if (hydratedMenuIdRef.current !== menuId) {
      hydratedMenuIdRef.current = menuId
      userEditedRef.current = false
      setQtyByRecipe(fromMenu)
      return
    }

    if (userEditedRef.current) {
      return
    }

    if (Object.keys(fromMenu).length === 0) {
      return
    }

    setQtyByRecipe(prev => {
      if (salesMixDraftHasLocalEdits(prev)) {
        return prev
      }
      return fromMenu
    })
  }, [menuId, initialSalesMix, panelRecipes])

  const preview = useMemo(
    () =>
      buildMenuSalesMixPanelPreview({
        panelRecipes,
        qtyByRecipe,
      }),
    [panelRecipes, qtyByRecipe],
  )

  useEffect(() => {
    onPreviewChange?.({
      hasSalesMix: preview.hasSalesMix,
      weightedGp: preview.weightedGp,
      estimatedTotalGp: preview.estimatedTotalGp,
    })
  }, [onPreviewChange, preview])

  const handleQtyChange = (recipeId, value) => {
    userEditedRef.current = true
    setQtyByRecipe(prev => ({
      ...prev,
      [recipeId]: value,
    }))
  }

  const handleSave = useCallback(async () => {
    if (!menuId || saving || disabled) {
      return
    }

    if (salesMixQtyDraftHasInvalidValues(qtyByRecipe)) {
      Modal.warning({
        title: copy.salesMixInvalidQtyTitle,
        content: copy.salesMixInvalidQtyBody,
      })
      return
    }

    const qtySnapshot = { ...qtyByRecipeRef.current }
    const entries = buildSalesMixSaveEntries(panelRecipes, qtySnapshot)

    const localPositive = panelRecipes.some(
      recipe => Number(readQtyForRecipe(qtySnapshot, recipe.id)) > 0,
    )

    try {
      setSaving(true)
      const response = await dispatch(updateMenuSalesMix(menuId, entries))

      const menusState = store.getState().menus
      const savedMenu = resolveSavedMenuAfterSalesMixSave(
        menuId,
        response,
        menusState,
      )
      const savedSalesMix = savedMenu?.salesMix || []
      const sentPositive = entries.some(entry => Number(entry?.expectedQty) > 0)
      const savedPositive = salesMixHasPositiveQty(savedSalesMix)

      if (localPositive && !sentPositive) {
        throw new Error(
          'Sales mix could not be read for save. Please re-enter quantities and try again.',
        )
      }

      if (sentPositive && !savedPositive) {
        throw new Error(
          'Sales mix did not save — recipe ids may not match this menu. Close and reopen the menu, then try again.',
        )
      }

      const nextQty = buildQtyDraftAfterSalesMixSave({
        savedSalesMix,
        entries,
        panelRecipes,
        qtyByRecipe: qtySnapshot,
      })

      setQtyByRecipe(nextQty)
      userEditedRef.current = false

      const savedPreview = buildMenuSalesMixPanelPreview({
        panelRecipes,
        qtyByRecipe: nextQty,
      })

      onPreviewChange?.({
        hasSalesMix: savedMenu?.hasSalesMix ?? savedPreview.hasSalesMix,
        weightedGp: savedMenu?.weightedGp ?? savedPreview.weightedGp,
        estimatedTotalGp:
          savedMenu?.estimatedTotalGp ?? savedPreview.estimatedTotalGp,
      })
    } catch (error) {
      const message = error?.message || copy.errorGeneric
      if (
        message.includes('could not be read') ||
        message.includes('did not save')
      ) {
        Modal.error({
          title: copy.salesMixInvalidQtyTitle,
          content: message,
        })
      }
    } finally {
      setSaving(false)
    }
  }, [
    copy.errorGeneric,
    copy.salesMixInvalidQtyTitle,
    disabled,
    dispatch,
    menuId,
    onPreviewChange,
    panelRecipes,
    qtyByRecipe,
    saving,
    store,
  ])

  return (
    <MenuSalesMixPanel
      recipes={toMenuSalesMixPanelRecipes(panelRecipes)}
      qtyByRecipe={qtyByRecipe}
      preview={preview}
      onQtyChange={handleQtyChange}
      onSave={handleSave}
      saving={saving}
      disabled={disabled || saving}
    />
  )
}

export default MenuSalesMixTab
